import { useDraggable, useDroppable } from '@dnd-kit/core';
import { GridRow, GridRowProps } from '@mui/x-data-grid-pro';
import { forwardRef, useRef } from 'react';
import { FolderContent } from '@/api/generatedApi';
import { getDropTargetStyles, applyRowPinnedCellStyles, DND_NO_SELECT_STYLES } from '@/components/dnd';
import { useBulkSelectionContext } from '@/components/ContentsTable/BulkSelectionContext';
import { isAsset } from '@/utils/assets';
import { isFolder, getContentParentFolderId } from '@/utils/folders';

export interface DragStateReference {
  draggedContents: FolderContent[];
  dropTargetFolderId?: string;
}

export const DraggableFolderContentRow = forwardRef<
  HTMLDivElement,
  GridRowProps & { dragStateRef?: React.RefObject<DragStateReference> }
>((properties, reference) => {
  const { row, dragStateRef, ...restProperties } = properties;
  const { draggedContents = [], dropTargetFolderId } = dragStateRef?.current ?? {};
  const bulkSelection = useBulkSelectionContext<FolderContent>();

  const rowContent = row as FolderContent | undefined;
  const rowIsFolder = isFolder(rowContent);
  const folder = rowIsFolder ? rowContent : undefined;
  const rowId = String(restProperties.rowId);
  const isDraggable = rowIsFolder || isAsset(rowContent);

  const selectedRows = bulkSelection?.selectedRows ?? [];
  const isSelected = rowContent && selectedRows.some(r => r.id === rowContent.id);
  const singleItemToDrag = rowContent ? [rowContent] : [];
  const contentsToDrag = isSelected && selectedRows.length > 0 ? selectedRows : singleItemToDrag;

  const {
    attributes,
    listeners,
    setNodeRef: setDragReference,
    isDragging: isDraggingThis,
  } = useDraggable({
    id: `drag-${rowId}`,
    data: { contents: contentsToDrag },
    disabled: !isDraggable,
  });

  const { setNodeRef: setDropReference, isOver } = useDroppable({
    id: `drop-${rowId}`,
    data: { type: 'folder-drop-target', folderId: folder?.id, folderName: folder?.name },
    disabled: !rowIsFolder,
  });

  const isPartOfBulkDrag = rowContent && draggedContents.some(c => c.id === rowContent.id);
  const isDragging = isDraggingThis || isPartOfBulkDrag;

  const isValidDropTarget = !!(
    folder &&
    draggedContents.some(content => {
      if (isFolder(content) && content.id === folder.id) return false;
      return getContentParentFolderId(content) !== folder.id;
    })
  );

  const shouldShowDropTarget = (isOver || dropTargetFolderId === folder?.id) && isValidDropTarget;
  const previousDropTargetState = useRef(shouldShowDropTarget);

  if (previousDropTargetState.current !== shouldShowDropTarget) {
    previousDropTargetState.current = shouldShowDropTarget;
    const rowElement = document.querySelector(`.MuiDataGrid-row[data-id="${rowId}"]`) as HTMLElement;
    if (rowElement) {
      requestAnimationFrame(() => applyRowPinnedCellStyles(rowElement, shouldShowDropTarget));
    }
  }

  const setNodeReference = (node: HTMLDivElement | null) => {
    if (isDraggable) setDragReference(node);
    if (rowIsFolder) setDropReference(node);
    if (typeof reference === 'function') reference(node);
    else if (reference) reference.current = node;

    if (node && shouldShowDropTarget) {
      requestAnimationFrame(() => applyRowPinnedCellStyles(node, shouldShowDropTarget));
    }
  };

  return (
    <GridRow
      {...restProperties}
      row={row}
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
      ref={setNodeReference}
      className={
        shouldShowDropTarget ? `${restProperties.className ?? ''} folder-drop-target`.trim() : restProperties.className
      }
      style={{
        ...getDropTargetStyles({
          isDropTarget: shouldShowDropTarget,
          isDragging,
          baseStyle: restProperties.style,
          includeTransition: true,
          includeNoSelect: isDraggable,
        }),
        cursor: isDraggable ? 'grab' : 'default',
        ...(isDraggable ? DND_NO_SELECT_STYLES : {}),
      }}
    />
  );
});

DraggableFolderContentRow.displayName = 'DraggableFolderContentRow';
